import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams, Link } from "react-router";
import {
  Typography,
  Box,
  LinearProgress,
  Card,
  CardContent,
  Stack,
  Button,
} from "@mui/material";
import PatientPlanDetails from "./PatientPlanDetails";

interface PatientData {
  patientName: string;
  patientId: string;
  dateOfBirth: string;
  oldPlanName: string;
  newPlanName: string;
}

const PlanTracking = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { recipeName } = useParams<{ recipeName: string }>();
  const patientData = location.state as PatientData;

  const [currentStep, setCurrentStep] = useState(0);
  const [progress, setProgress] = useState(0);

  const planSteps = [
    { title: "Initialising", duration: 2000, timeMinutes: 2 },
    { title: "Plan setup", duration: 2000, timeMinutes: 3 },
    { title: "Clinical goal integration", duration: 2000, timeMinutes: 2 },
    { title: "Plan optimisation", duration: 8000, timeMinutes: 20 },
    { title: "Plan completion", duration: 2000, timeMinutes: 2 },
  ];

  // Calculate elapsed time based on completed steps
  const getElapsedTime = () => {
    let totalMinutes = 0;
    for (let i = 0; i < currentStep; i++) {
      totalMinutes += planSteps[i].timeMinutes;
    }
    return totalMinutes;
  };

  useEffect(() => {
    if (currentStep < planSteps.length) {
      const timer = setTimeout(() => {
        setCurrentStep((prev) => prev + 1);
        setProgress((prev) => prev + 100 / planSteps.length);
      }, planSteps[currentStep].duration);

      return () => clearTimeout(timer);
    }
  }, [currentStep, navigate, patientData]);

  const currentStepData =
    planSteps[currentStep] || planSteps[planSteps.length - 1];
  const elapsedTime = getElapsedTime();
  const isComplete = currentStep >= planSteps.length;

  // Get the encoded recipe name for URLs
  const encodedRecipeName = encodeURIComponent(recipeName || "");

  return (
    <Stack height={1} gap={2}>
      <Typography variant="body1" color="text.secondary" marginTop={2}>
        Elapsed time: {elapsedTime} mins
      </Typography>

      <Card>
        <CardContent>
          <Stack spacing={2}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="body1">{currentStepData.title}</Typography>
              <Typography variant="body2" color="text.secondary">
                Machine TB5416
              </Typography>
            </Box>

            <LinearProgress
              sx={{ marginBottom: 2, marginTop: 3 }}
              variant="determinate"
              color="secondary"
              value={progress}
            />

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {Math.round(progress)}% Complete
              </Typography>
              {currentStep < planSteps.length && (
                <Typography variant="body2" color="text.secondary">
                  Step {currentStep + 1} of {planSteps.length}
                </Typography>
              )}
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Patient and Plan Details */}
      {patientData && <PatientPlanDetails patientData={patientData} />}

      {/* Buttons */}
      <Stack direction="row" justifyContent="space-between" marginTop="auto">
        <Button
          component={Link}
          to={`/blueprint/${encodedRecipeName}/generate`}
          variant="outlined"
          color="primary"
        >
          Back
        </Button>
        <Button
          component={Link}
          to={`/blueprint/${encodedRecipeName}/compare`}
          variant="contained"
          color="primary"
          disabled={!isComplete}
          state={patientData}
        >
          Continue
        </Button>
      </Stack>
    </Stack>
  );
};

export default PlanTracking;
